<script setup>
// "Key" dialog for the americano standings table: every column the table can
// show (the full expanded set), with what each one means — the same meanings
// the desktop header tooltips carry, for screens where hover doesn't exist.
import {computed} from 'vue';
import TlDialog from '~/components/TlDialog.vue';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  // Division flag: set/game tiebreak columns exist only with per-set results.
  hasSetScores: {type: Boolean, default: false},
});
const emit = defineEmits(['update:modelValue']);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

// Ranking runs F first; ties break S± → G± → +/-, so the +/- meaning names
// its place in that cascade (and becomes THE tiebreaker without set data).
const entries = computed(() => [
  {label: 'P', meaning: 'Matches played'},
  {label: 'F', meaning: 'Points for: awarded by the scoring system; the table ranks on this first'},
  {label: 'A', meaning: 'Points against: conceded to opponents'},
  {
    label: '+/-',
    meaning: props.hasSetScores
        ? 'Points difference: the third tiebreaker, after set and game difference'
        : 'Points difference: the tiebreaker on equal points',
  },
  ...(props.hasSetScores ? [
    {label: 'S±', meaning: 'Set difference: the first tiebreaker on equal points'},
    {label: 'G±', meaning: 'Game difference: the second tiebreaker (a champions tiebreak counts as one game)'},
  ] : []),
]);
</script>

<template>
  <tl-dialog v-model="open" title="Table key" :max-width="420">
    <div class="atk">
      <div v-for="entry in entries" :key="entry.label" class="atk-row">
        <span class="atk-label">{{ entry.label }}</span>
        <span class="atk-meaning">{{ entry.meaning }}</span>
      </div>
    </div>
  </tl-dialog>
</template>

<style scoped lang="scss">
.atk {
  padding: 4px 18px 18px;
}

.atk-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 5px 0;
}

.atk-label {
  flex-shrink: 0;
  width: 38px;
  padding: 3px 0;
  text-align: center;
  font-size: 11.5px;
  font-weight: 800;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 7px;
}

.atk-meaning {
  padding-top: 3px;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgba(var(--v-theme-charcoal), 0.8);
}
</style>
